import * as React from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import './index.scss';

export const PasswordField = ({
  label: labelProps,
  name = 'password',
  value,
  onChange,
  className,
  onClear,
  error,
  withValidation,
  ...other
}) => {
  const [show, toggle] = React.useState(false);
  const { t } = useTranslation('components');
  const label = labelProps || t('password_field.password_label');

  return (
    <div className={classnames('password-field', error && 'password-field_error', className)}>
      <div className="password-field__input-group">
        <input
          type={show ? 'text' : 'password'}
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          placeholder=" "
          className={classnames('password-field__input', value && 'password-field__input-filled')}
          {...other}
        />
        <label className="password-field__label" htmlFor={name}>
          {label}
        </label>
        {onClear && (
          <svg
            onClick={onClear}
            className={classnames(
              'password-field__clear',
              !value && 'password-field__clear-hidden',
            )}
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8 0C3.57333 0 0 3.57333 0 8C0 12.4267 3.57333 16 8 16C12.4267 16 16 12.4267 16 8C16 3.57333 12.4267 0 8 0ZM10.3111 11.3778C9.54667 10.6133 8.76444 9.83111 8 9.06667C7.23556 9.83111 6.45333 10.6133 5.68889 11.3778C4.99556 12.0711 3.94667 11.0044 4.62222 10.3111C5.38667 9.54667 6.16889 8.76444 6.93333 8C6.16889 7.23556 5.38667 6.45333 4.62222 5.68889C3.92889 4.99556 4.99556 3.94667 5.68889 4.62222C6.45333 5.38667 7.23556 6.16889 8 6.93333C8.76444 6.16889 9.54667 5.38667 10.3111 4.62222C11.0044 3.92889 12.0533 4.99556 11.3778 5.68889C10.6133 6.45333 9.83111 7.23556 9.06667 8C9.83111 8.76444 10.6133 9.54667 11.3778 10.3111C12.0533 10.9867 10.9867 12.0533 10.3111 11.3778Z"
              fill="#D8D6DA"
            />
          </svg>
        )}
      </div>

      <div
        className={classnames('password-field__error', error && 'password-field__error_visible')}
      >
        {error}
      </div>

      <div className="password-field__bottom">
        <div className="password-field__rules-wrap">
          {withValidation && (
            <ul className="password-field__rules">
              <li className={classnames(value.length >= 8 && 'valid')}>
                {t('password_field.eight_characters_minimum')}
              </li>
              <li className={classnames(/[a-z]/.test(value) && 'valid')}>
                {t('password_field.one_lowercase_character')}
              </li>
              <li className={classnames(/[A-Z]/.test(value) && 'valid')}>
                {t('password_field.one_uppercase_character')}
              </li>
              <li className={classnames(/(?=.*[-_+!@#$%^&*])/g.test(value) && 'valid')}>
                {t('password_field.one_special_character')}
              </li>
              <li className={classnames(/\d/.test(value) && 'valid')}>
                {t('password_field.one_number')}
              </li>
            </ul>
          )}
        </div>
        {value &&
          (show ? (
            <div
              className="password-field__toggle password-field__toggle_visible"
              onClick={() => toggle(!show)}
            >
              <svg
                width="16"
                height="6"
                viewBox="0 0 16 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.948 2.23674C15.8779 2.35889 15.7504 2.42665 15.6183 2.42665C15.5542 2.42665 15.4879 2.41014 15.4275 2.37512L14.2001 1.66132C13.9689 1.9039 13.6992 2.13026 13.3977 2.3381L14.2761 3.37706C14.4122 3.53679 14.3917 3.77795 14.2308 3.9132C14.1596 3.97441 14.0719 4.00345 13.9857 4.00345C13.8769 4.00345 13.7698 3.95761 13.6944 3.86821L12.7371 2.73643C12.3559 2.93802 11.9394 3.11426 11.4926 3.26317L12.0316 4.64408C12.1085 4.84026 12.0114 5.0612 11.8144 5.13665C11.7694 5.15488 11.7224 5.16427 11.6763 5.16427C11.5239 5.16427 11.3802 5.07202 11.3215 4.92055L10.7575 3.47472C10.3187 3.58377 9.85831 3.66662 9.38168 3.72158L9.67552 5.35077C9.7131 5.55804 9.57529 5.75621 9.3683 5.79437C9.34495 5.79835 9.32246 5.80034 9.29968 5.80034C9.11831 5.80034 8.95915 5.67108 8.92555 5.48686L8.61862 3.78621C8.41447 3.79589 8.20862 3.80158 8.00049 3.80158C7.79207 3.80158 7.5865 3.79589 7.3815 3.78621L7.07542 5.48686C7.04182 5.67079 6.88152 5.80034 6.70044 5.80034C6.6788 5.80034 6.65602 5.79835 6.63267 5.79437C6.4254 5.75621 6.28816 5.55804 6.32546 5.35077L6.61929 3.72158C6.14266 3.66662 5.6817 3.58377 5.24322 3.47472L4.67947 4.92055C4.61996 5.07202 4.47618 5.16427 4.32385 5.16427C4.27801 5.16427 4.23103 5.15516 4.18547 5.13665C3.98958 5.06092 3.89249 4.84026 3.96908 4.64408L4.50807 3.26317C4.06133 3.11455 3.64478 2.93802 3.26268 2.73643L2.3063 3.86821C2.23027 3.95733 2.1235 4.00345 2.01502 4.00345C1.92818 4.00345 1.84077 3.97413 1.76959 3.9132C1.60872 3.77795 1.58822 3.53679 1.72432 3.37706L2.60184 2.3381C2.30003 2.13054 2.03154 1.9039 1.80006 1.66132L0.572614 2.37512C0.512537 2.41014 0.445912 2.42665 0.38128 2.42665C0.250022 2.42665 0.121896 2.3586 0.0515695 2.23674C-0.054063 2.05509 0.00772185 1.8219 0.189945 1.71598L1.32428 1.05713C1.21495 0.883735 1.12355 0.704929 1.05095 0.519573C0.974359 0.323683 1.07117 0.102738 1.26649 0.0264318C1.46266 -0.0504437 1.68361 0.0460777 1.7602 0.241968C2.32196 1.67669 4.44144 2.76149 6.98715 2.99325C7.03584 2.98243 7.73512 3.04023 8.00077 3.04023C8.26585 3.04023 8.96485 2.98243 9.01353 2.99325C11.5595 2.76177 13.6796 1.67669 14.2411 0.241968C14.3179 0.0460777 14.5389 -0.0501589 14.7342 0.0264318C14.9301 0.102738 15.0269 0.323683 14.9506 0.519573C14.8777 0.704644 14.7863 0.883735 14.677 1.05713L15.8116 1.71598C15.9924 1.8219 16.0542 2.05537 15.948 2.23674Z"
                  fill="black"
                />
              </svg>
              <span>{t('password_field.hide')}</span>
            </div>
          ) : (
            <div
              className="password-field__toggle password-field__toggle_hidden"
              onClick={() => toggle(!show)}
            >
              <svg
                width="16"
                height="10"
                viewBox="0 0 16 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.00005 7.14266C9.20496 7.14266 10.1817 6.18335 10.1817 4.99998C10.1817 3.81661 9.20496 2.8573 8.00005 2.8573C6.79513 2.8573 5.81836 3.81661 5.81836 4.99998C5.81836 6.18335 6.79513 7.14266 8.00005 7.14266Z"
                  fill="black"
                />
                <path
                  d="M8 0C3.63627 0 0 4.64277 0 5C0 5.35688 3.63627 10 8 10C12.3636 10 16 5.35723 16 5C16 4.64259 12.3637 0 8 0ZM8 8.57125C5.99111 8.57125 4.36373 6.97262 4.36373 5C4.36373 3.02685 5.99111 1.42875 8 1.42875C10.0087 1.42875 11.6363 3.02685 11.6363 5C11.6363 6.97262 10.0087 8.57125 8 8.57125Z"
                  fill="black"
                />
              </svg>
              <span>{t('password_field.show')}</span>
            </div>
          ))}
      </div>
    </div>
  );
};
